import { Injectable } from '@angular/core';

// If you import a module but never use any of the imported values other than as TypeScript types,
// the resulting javascript file will look as if you never imported the module at all.
import { ipcRenderer, webFrame } from 'electron';
import * as childProcess from 'child_process';
import * as fs from 'fs';
import * as path from 'path';

import { EnvironmentService } from '../../shared/environment.service';

@Injectable({
    providedIn: 'root'
})
export class ElectronEnvironmentService extends EnvironmentService {
    ipcRenderer: typeof ipcRenderer;
    webFrame: typeof webFrame;
    childProcess: typeof childProcess;
    fs: typeof fs;
    path = path;

    private currentDir: string;
    private printer: string;

    constructor() {
        super();
        // Conditional imports
        if (this.isElectron()) {
            this.ipcRenderer = window.require('electron').ipcRenderer;
            this.webFrame = window.require('electron').webFrame;

            this.fs = window.require('fs');

            this.childProcess = window.require('child_process');
            this.childProcess.exec('node -v', (error, stdout, stderr) => {
                if (error) {
                    console.error(`error: ${error.message}`);
                    return;
                }
                if (stderr) {
                    console.error(`stderr: ${stderr}`);
                    return;
                }
                console.log(`stdout:\n${stdout}`);
            });

            // Notes :
            // * A NodeJS's dependency imported with 'window.require' MUST BE present in `dependencies` of both `app/package.json`
            // and `package.json (root folder)` in order to make it work here in Electron's Renderer process (src folder)
            // because it will loaded at runtime by Electron.
            // * A NodeJS's dependency imported with TS module import (ex: import { Dropbox } from 'dropbox') CAN only be present
            // in `dependencies` of `package.json (root folder)` because it is loaded during build phase and does not need to be
            // in the final bundle. Reminder : only if not used in Electron's Main process (app folder)

            // If you want to use a NodeJS 3rd party deps in Renderer process,
            // ipcRenderer.invoke can serve many common use cases.
            // https://www.electronjs.org/docs/latest/api/ipc-renderer#ipcrendererinvokechannel-args
        }
    }

    isElectron(): boolean {
        return !!(window && window.process && window.process.type);
    }

    startApplication(appPath: string, parameters: string, outFn: (outText?: string, errText?: string, errMessage?: string) => void): void {
        const resolvedAppPath = this.constructPath(appPath);
        const finalPath = `"${resolvedAppPath}" ${parameters}`;
        console.log('Starting application', finalPath);
        this.childProcess.exec(finalPath, (err, stdout, stderr) => {
            if (err) {
                console.error(err);
            }
            if (outFn) {
                let errValue: string | undefined;
                if (err) {
                    errValue = `Code: ${err.code} ; Message: ${err.message} ; Name: ${err.name}`;
                }
                outFn(stdout, stderr, errValue);
            }
        });
    }

    loadFileAsString(filePath: string): string {
        const resolvedPath = this.constructPath(filePath);
        const buff = this.fs.readFileSync(resolvedPath);
        const result = buff.toString();
        return result;
    }

    saveStringToFile(filePath: string, content: string): void {
        const resolvedPath = this.constructPath(filePath);
        this.fs.writeFileSync(resolvedPath, content);
    }

    constructPath(sourcePath: string): string {
        if (this.path.isAbsolute(sourcePath)) {
            return sourcePath;
        } else {
            return this.path.join(this.getCurrentDir(), sourcePath);
        }
    }

    getCurrentDir(): string {
        if (!this.currentDir) {
            this.currentDir = this.path.resolve('.'); // this.path.dirname(this.childProcess. this.remote.app.getPath('exe'));
        }
        return this.currentDir;
    }

    setSelectedPrinter(printer: string): void {
        this.printer = printer;
    }

    getSelectedPrinter(): string {
        return this.printer;
    }

    runExecutable(
        executablePath: string,
        cmdLine: string,
        callback: (outTxt: string | undefined, errTxt: string | undefined, errMsg: string | undefined) => void
    ): void {
        this.startApplication(executablePath, cmdLine, (outTxt, errTxt, errMsg) => {
            if (callback) {
                callback(outTxt, errTxt, errMsg);
            }
        });
    }
}
